import React, { useEffect, useMemo, useState } from 'react';
import { ExampleLinks, setExampleWorkflow } from '../../../api/cafAutomation';
import { CafCorpusEntry, UpdateEntryPayload, getCafCorpusEntry, updateCafEntry } from '../../../api/cafCorpus';
import { NoteField } from '../automation/formBits';
import { formatCount } from '../format';
import { LabelDraft, LabelForm, draftFromEntry, draftIsValid, draftToWrite, stanceField, useClaimFieldOptions } from '../LabelForm';
import { useFinalResponseSupported } from '../finalResponseSupport';
import { senderEmail } from '../lenders';
import { Drawer } from '../Overlay';
import { MSG_NOT_CONFIRMED, isClash, runSave } from '../saveEngine';
import { useToast } from '../ToastContext';
import { ConflictWarnings, useConflictGate } from './ConflictWarnings';
import { schemaForComparison } from './guidanceComparison';
import { readBackEntry, useCurrentEntry } from './useCurrentEntry';
import { contradicts, matchingSeed } from './workflowFit';

/** The reason sent when no note is typed: works on an older backend too (spec §6.7). */
export const DEFAULT_EDIT_REASON = 'Edited in Teach the AI';
const NONE = 'none';
const OPTION_TAGS: Record<string, string> = {
  on: 'On', watching: 'Watching', paused: 'Paused', draft: "Draft: won't run until it's watching or on",
};

/** #705: the workflow follows what the example says should happen. */
export const followSentence = (from: string, to: string | null): string => (to
  ? `When you save, the workflow changes from "${from}" to "${to}" to match what should happen.`
  : `When you save, "${from}" is removed. No built-in workflow matches what should happen, so this example will have no workflow.`);

/** PATCH preserves omitted fields, unlike the replacement POST comparisons. */
const matchesEdit = (fresh: CafCorpusEntry, previous: CafCorpusEntry, payload: UpdateEntryPayload): boolean => {
  const action = payload.action ?? previous.correct_action;
  const schema = payload.extraction_schema === undefined ? previous.extraction_schema : payload.extraction_schema;
  const archiveRefs = payload.archive_override_refs === undefined
    ? (action === 'AUTO_ARCHIVE' ? previous.archive_override_refs : false)
    : payload.archive_override_refs;
  const classification = payload.categoryOverride === undefined ? previous.category_override : payload.categoryOverride;
  return fresh.reason === payload.reason
    && fresh.correct_action === action
    && fresh.note === (payload.note ?? previous.note)
    && JSON.stringify(schemaForComparison(fresh.extraction_schema)) === JSON.stringify(schemaForComparison(schema))
    && Boolean(fresh.archive_override_refs) === Boolean(archiveRefs)
    && (fresh.category_override ?? null) === (classification ?? null)
    && (fresh.final_response_stance ?? null) === (payload.final_response_stance ?? previous.final_response_stance ?? null);
};

interface Props {
  entry: CafCorpusEntry;
  lenderOf: (sender: string) => string | null;
  onClose: () => void;
  onSaved: (updated: CafCorpusEntry | null) => void;
  /** Automation links; null or left out (older backend): no Workflow field. */
  links?: ExampleLinks | null;
  /** Admins change the workflow; curators see it as text. */
  canAutomate?: boolean;
  onLinked?: () => void;
  onOpenWorkflow?: (workflowId: string) => void;
}

export const EditDrawer: React.FC<Props> = ({
  entry: given, lenderOf, onClose, onSaved, links = null, canAutomate = false, onLinked, onOpenWorkflow,
}) => {
  const toast = useToast();
  const gate = useConflictGate();
  // Final fix wave C1/C2: an older backend neither asks for nor takes the final-response setting.
  const rules = { finalResponse: useFinalResponseSupported() };
  const claimFields = useClaimFieldOptions();
  const { entry: currentEntry, ready, problem: loadProblem } = useCurrentEntry(given);
  // Keep the user's edits after an example save or a version reload.
  const [latestEntry, setLatestEntry] = useState<CafCorpusEntry | null>(null);
  const [needsReload, setNeedsReload] = useState(false);
  const [failedLinks, setFailedLinks] = useState<ExampleLinks | null>(null);
  const entry = latestEntry ?? currentEntry;
  const original = useMemo(() => draftFromEntry(entry), [entry]);
  const [draft, setDraft] = useState<LabelDraft | null>(null);
  const [note, setNote] = useState('');
  // null: not touched; NONE: an explicit None; otherwise a workflow id.
  const [choice, setChoice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');
  const value = draft ?? original;
  const write = draftToWrite(value, claimFields, entry, rules);
  const unchanged = JSON.stringify(write) === JSON.stringify(draftToWrite(original, claimFields, entry, rules));

  const linkRow = links ? links.links[entry.id] : undefined;
  const current = linkRow ? linkRow.workflow_id : null;
  const isArchive = value.action === 'AUTO_ARCHIVE';
  const stance = value.finalResponse || null;
  const workflowNamed = (workflowId: string | null) => (workflowId && links
    ? links.workflows.find((item) => item.workflow_id === workflowId) ?? null : null);
  /** A linked workflow's built-in key, or null when an admin built it by hand. */
  const keyOf = (workflowId: string | null): string | null => (links && workflowId
    ? Object.keys(links.seeds_by_action).find((key) => links.seeds_by_action[key] === workflowId) ?? null : null);
  const misfits = (workflowId: string | null): boolean => {
    const key = keyOf(workflowId);
    return workflowId !== null && key !== null && contradicts(key, value.action, stance);
  };
  // #705: the built-in workflow that does what the example now says, or null when there is none.
  const matchingKey = isArchive ? null : matchingSeed(value.action, stance);
  const matchingId = links && matchingKey ? links.seeds_by_action[matchingKey] ?? null : null;
  const matching = workflowNamed(matchingId) ? matchingId : null;
  const isFinal = (setting: string) => setting === 'final_response';
  const whatChanged = value.action !== original.action || isFinal(value.finalResponse) !== isFinal(original.finalResponse);
  // What the save itself leaves on the example (the server does the same): a built-in workflow
  // that no longer does what the example says moves to the matching one, or to None.
  const onSave = isArchive || !linkRow || current === null || !misfits(current) ? current : matching;
  // An admin's starting choice. Q20: an example that never had a workflow starts on the matching
  // built-in. A workflow built by hand is kept until what should happen changes.
  const suggested = !linkRow && !isArchive ? matching : null;
  const followed = !linkRow ? suggested : onSave !== current ? onSave
    : current !== null && keyOf(current) === null && whatChanged && !isArchive ? matching : current;
  const chosen = !canAutomate || isArchive ? onSave : choice === null ? followed : choice === NONE ? null : choice;
  const workflowChanged = Boolean(links) && canAutomate && !isArchive
    && (linkRow ? chosen !== current : chosen !== null || choice === NONE);
  const chosenWorkflow = workflowNamed(chosen);
  const matchingWorkflow = workflowNamed(matching);
  const movedFrom = linkRow && chosen !== current && (!canAutomate || choice === null) ? workflowNamed(current) : null;
  const canSave = ready && !saving && !needsReload && !failedLinks && draftIsValid(value, rules) && (!unchanged || workflowChanged);

  useEffect(() => {
    if (failedLinks && links !== failedLinks) setFailedLinks(null);
  }, [failedLinks, links]);

  const reloadLatest = async () => {
    try {
      const fresh = await getCafCorpusEntry(entry.id, entry.sender);
      setLatestEntry(fresh);
      setNeedsReload(false);
      setProblem('');
      onLinked?.();
    } catch {
      setProblem("Couldn't load the latest example. What you typed is kept. Try again.");
    }
  };

  const save = async () => {
    if (!canSave) return;
    const typed = note.trim();
    setSaving(true);
    setProblem('');
    let saved: CafCorpusEntry | null = null;
    // The example's link as the server now has it: the save may have moved it (#705).
    let linkNow = linkRow ? { workflow_id: current, revision: linkRow.revision } : null;
    let movedBySave = false;
    if (!unchanged) {
      const checked = await gate.confirmOrWarn({ source: { kind: 'entry', id: entry.id }, sender: entry.sender, action: write.action, final_response_stance: write.final_response_stance, workflow_id: chosen ?? null });
      if (!checked.proceed) { setSaving(false); return; }
      const expected = entry.version || 0;
      const payload: UpdateEntryPayload = {
        action: write.action,
        note: write.note,
        ...stanceField(write),
        extraction_schema: write.extraction_schema,
        ...(write.archive_override_refs !== undefined ? { archive_override_refs: write.archive_override_refs } : {}),
        ...(write.categoryOverride !== undefined ? { categoryOverride: write.categoryOverride } : {}),
        expected_version: expected,
        ...(checked.warnings.length ? { acknowledged_warnings: checked.warnings } : {}),
        reason: typed || DEFAULT_EDIT_REASON,
        ...(entry.version ? {} : { reviewed_token: entry.compare_token }),
      };
      const result = await runSave(() => updateCafEntry(entry.id, payload), {
        readBack: () => readBackEntry(entry, expected, (fresh) => matchesEdit(fresh, entry, payload)),
      });
      if (!result.ok) {
        setSaving(false);
        setProblem(result.message);
        toast.failed(result.message, !isClash(result.message) && result.message !== MSG_NOT_CONFIRMED
          ? () => { void save(); } : undefined);
        return;
      }
      saved = result.value;
      if (saved) setLatestEntry(saved);
      else setNeedsReload(true);
      const moved = saved?.workflow_link;
      if (moved) {
        linkNow = { workflow_id: moved.workflow_id, revision: moved.revision };
        movedBySave = moved.changed;
      }
    }
    // An admin's own choice still wins, even over a move the save just made; skip the write when
    // the save already left the link there.
    const adminChose = workflowChanged || (Boolean(links) && canAutomate && !isArchive && choice !== null);
    const needsLink = adminChose && (linkNow ? linkNow.workflow_id !== chosen : chosen !== null || choice === NONE);
    if (needsLink) {
      const linked = await runSave(() => setExampleWorkflow(entry.id, {
        workflow_id: chosen, sender: entry.sender, expected_revision: linkNow ? linkNow.revision : 0, ...(typed ? { note: typed } : {}),
      }));
      if (!linked.ok) {
        setSaving(false);
        // A fresh links response is needed before another CAS attempt, especially when the first reply was lost.
        setFailedLinks(links);
        if (!unchanged) {
          // The example is confirmed saved. Keep the chosen workflow and reload versions before retrying.
          const partial = linked.message === MSG_NOT_CONFIRMED
            ? "Saved the example, but couldn't confirm its workflow. Wait and reload before trying again."
            : `Saved the example, but couldn't change its workflow. ${linked.message.replace("Couldn't save. ", '')}`;
          setProblem(partial);
          toast.failed(partial);
          onSaved(saved);
          return;
        }
        setProblem(linked.message);
        toast.failed(linked.message, !isClash(linked.message) && linked.message !== MSG_NOT_CONFIRMED
          ? () => { void save(); } : undefined);
        return;
      }
      if (onLinked) onLinked();
    } else if (movedBySave && onLinked) {
      onLinked();
    }
    setSaving(false);
    const finalId = needsLink ? chosen : linkNow ? linkNow.workflow_id : null;
    const finalWorkflow = workflowNamed(finalId);
    const workflowMoved = (needsLink || movedBySave) && finalId !== current;
    if (workflowMoved && finalWorkflow) {
      const workflowId = finalWorkflow.workflow_id;
      toast.saved(`Saved. It now uses "${finalWorkflow.name}".`,
        onOpenWorkflow ? { label: 'See it in Automation', onClick: () => onOpenWorkflow(workflowId) } : undefined);
    } else if (workflowMoved) {
      toast.saved('Saved. It no longer has a workflow.');
    } else {
      toast.saved('Saved.');
    }
    onSaved(saved);
    onClose();
  };

  const fieldId = `edit-${entry.id}-workflow`;
  const workflowField = links ? (
    <div className="cur-field">
      {canAutomate ? (
        <>
          <label htmlFor={fieldId}>Workflow <span className="cur-opt">(optional)</span></label>
          <select id={fieldId} className="cur-input" value={chosen ?? NONE} disabled={saving || !ready || isArchive}
            onChange={(event) => setChoice(event.target.value)}>
            <option value={NONE}>None</option>
            {links.workflows.map((item) => (
              <option key={item.workflow_id} value={item.workflow_id}>{`${item.name} (${OPTION_TAGS[item.state]})`}</option>
            ))}
          </select>
          {isArchive ? (
            <span className="cur-hint">{current
              ? 'This workflow stays linked but does not run while the example is archived. Change what should happen to edit its workflow.'
              : "An archived email has no claim, so it can't have a workflow."}</span>
          ) : (
            <span className="cur-hint">
              When the workflow and overall AI are on, it can run for future emails. While either is Watching, the AI records proposals without acting on emails.
            </span>
          )}
          {!isArchive && choice === null && suggested && <span className="cur-hint">Suggested: the built-in workflow for this action.</span>}
          {!isArchive && movedFrom && <div className="cur-alert cur-alert--info">{followSentence(movedFrom.name, chosenWorkflow?.name ?? null)}</div>}
          {!isArchive && chosenWorkflow?.state === 'on' && !misfits(chosen) && (
            <div className="cur-alert cur-alert--info">This workflow is on, so it runs for this example from now on.</div>
          )}
          {!isArchive && chosenWorkflow && misfits(chosen) && (
            <div className="cur-alert cur-alert--warn" role="alert">
              {`"${chosenWorkflow.name}" doesn't do what this example says should happen, so it won't run. `}
              {matchingWorkflow ? `Pick "${matchingWorkflow.name}" or None.` : 'Pick None.'}
            </div>
          )}
        </>
      ) : (
        <>
          <strong>Workflow</strong>
          <span>{chosenWorkflow?.name ?? 'None'}</span>
          {movedFrom
            ? <span className="cur-hint">{followSentence(movedFrom.name, chosenWorkflow?.name ?? null)} Only an admin can choose a different one.</span>
            : <span className="cur-hint">Only an admin can change this.</span>}
        </>
      )}
    </div>
  ) : null;

  return (
    <Drawer eyebrow="Edit approved example" title={lenderOf(entry.sender) ?? senderEmail(entry.sender)}
      subtitle={`${senderEmail(entry.sender)}${entry.group_size != null ? ` · covers ${formatCount(entry.group_size)} emails` : ''}`}
      onClose={onClose} busy={saving}
      footer={(
        <>
          {problem && (
            <div className="cur-alert cur-alert--error" role="alert">
              <span className="cur-grow">{problem}</span>
              {(isClash(problem) || problem === MSG_NOT_CONFIRMED || problem.startsWith('Saved the example, but')) && (
                <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => { void reloadLatest(); }}>Reload</button>
              )}
            </div>
          )}
          <span className="cur-hint cur-grow">{unchanged && !workflowChanged ? 'Change something to save.' : ''}</span>
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="ui-btn ui-btn--primary ui-btn--md" disabled={!canSave} onClick={() => { void save(); }}>
            {saving ? <><span className="cur-spin" aria-hidden="true" />Saving…</> : gate.conflicts.length ? 'Save anyway' : 'Save changes'}
          </button>
        </>
      )}>
      {loadProblem && <div className="cur-alert cur-alert--error" role="alert">{loadProblem}</div>}
      <LabelForm idPrefix={`edit-${entry.id}`} value={value} onChange={setDraft} claimFields={claimFields}
        disabled={saving || !ready} afterAction={workflowField} />
      <ConflictWarnings conflicts={gate.conflicts} proposal={{ action: value.action, final_response_stance: value.finalResponse, workflowName: chosenWorkflow?.name ?? null }} />
      <NoteField id={`edit-${entry.id}-note`} value={note} onChange={setNote} disabled={saving}
        placeholder="For example: Northbridge changed the wording of their holding letter." />
    </Drawer>
  );
};
